import classNames from "classnames";
import * as React from "react";
import Icon from "../../../components/atoms/Icon";
import { DrillContentItem } from "../useFetchStudyRecordContentsWithAnswers";
import { RetentionLevelBadge } from "./RetentionLevelBadge";

type Props = {
  content: DrillContentItem;
  contentIndex: number;
};

export const ContentItem = ({ content, contentIndex }: Props) => {
  if (!content.items || content.items.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {/* コンテンツ名 */}
      <div className="flex items-start gap-[10px]">
        {content.retentionLevel && (
          <RetentionLevelBadge level={content.retentionLevel} />
        )}
        <span className="text-[14px] font-bold leading-[1.4] text-gray-900">
          {content.name ?? `コンテンツ ${contentIndex + 1}`}
        </span>
      </div>

      {/* 問題一覧 */}
      <div className="flex flex-col">
        {content.items.map((item, itemIndex) => {
          const prevItem =
            itemIndex > 0 ? content.items?.[itemIndex - 1] : undefined;
          return (
            <div
              key={itemIndex}
              className={
                itemIndex > 0
                  ? prevItem?.answer != null
                    ? "mt-4"
                    : "mt-8"
                  : undefined
              }
            >
              <QuestionItem item={item} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

type QuestionItemProps = {
  item: NonNullable<DrillContentItem["items"]>[number];
};

const QuestionItem = ({ item }: QuestionItemProps) => {
  const [isHistoryExpanded, setIsHistoryExpanded] = React.useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] =
    React.useState(false);

  const DESCRIPTION_LIMIT = 200;
  const hasAnswerHistories = !!(
    item.answerHistories && item.answerHistories.length > 0
  );
  const currentAttempt = item.answerHistories?.find((h) => h.current);
  const isLongDescription = !!(
    item.description && item.description.length > DESCRIPTION_LIMIT
  );
  const displayDescription =
    !isDescriptionExpanded && isLongDescription
      ? (item.description?.substring(0, DESCRIPTION_LIMIT) ?? "") + "..."
      : (item.description ?? "");

  const hasAnswer = item.answer != null;

  return (
    <div className="flex flex-col gap-4">
      <ContentItemDescription
        description={displayDescription}
        choices={item.choices}
        isDescriptionExpanded={isDescriptionExpanded}
        setIsDescriptionExpanded={setIsDescriptionExpanded}
        isLongDescription={isLongDescription}
      />
      {item.image && <ContentItemImage src={item.image} />}
      {item.audio && <ContentItemAudio src={item.audio} />}
      {item.pdf && <ContentItemPdf href={item.pdf} />}
      {item.correctValue && <CorrectAnswer correctValue={item.correctValue} />}
      {hasAnswer && (
        <AnswerBox
          answer={item.answer}
          correct={item.correct ?? undefined}
          answerHistories={item.answerHistories?.map((h) => ({
            ...h,
            correct: h.correct ?? undefined,
          }))}
          hasAnswerHistories={hasAnswerHistories}
          currentAttemptNumber={currentAttempt?.attemptNumber}
          isHistoryExpanded={isHistoryExpanded}
          setIsHistoryExpanded={setIsHistoryExpanded}
        />
      )}
    </div>
  );
};

type ContentItemDescriptionProps = {
  description: string;
  choices?: string[];
  isDescriptionExpanded: boolean;
  setIsDescriptionExpanded: (expanded: boolean) => void;
  isLongDescription: boolean;
};

const ContentItemDescription = ({
  description,
  choices,
  isDescriptionExpanded,
  setIsDescriptionExpanded,
  isLongDescription,
}: ContentItemDescriptionProps) => {
  const shouldShowChoices = !isLongDescription || isDescriptionExpanded;

  return (
    <div className="flex flex-col gap-[10px] text-[14px] leading-[1.4] text-gray-900">
      <div>{description}</div>
      <ContentItemChoices choices={choices} isVisible={shouldShowChoices} />
      {isLongDescription && (
        <button
          type="button"
          className="flex items-center gap-[6px] text-[14px] font-bold leading-[14px] text-blue-400"
          onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
        >
          <Icon
            name="icon-arrow-down"
            className={classNames(
              "h-6 w-6 fill-blue-400 transition-transform",
              {
                "rotate-180": isDescriptionExpanded,
              },
            )}
          />
          すべて見る
        </button>
      )}
    </div>
  );
};

type ContentItemChoicesProps = {
  choices?: string[];
  isVisible: boolean;
};

const ContentItemChoices = ({
  choices,
  isVisible,
}: ContentItemChoicesProps) => {
  if (!isVisible || !choices || choices.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-x-[20px] gap-y-[8px]">
      {choices.map((choice, index) => (
        <span key={index}>{choice}</span>
      ))}
    </div>
  );
};

const ContentItemImage = ({ src }: { src: string }) => {
  return (
    <div className="md:max-w-[70%]">
      <img src={src} alt="" />
    </div>
  );
};

const ContentItemAudio = ({ src }: { src: string }) => {
  return <audio src={src} controls className="w-full md:w-[70%]" />;
};

const ContentItemPdf = ({ href }: { href: string }) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-[6px] text-[14px] font-bold leading-[14px] text-blue-400"
    >
      <Icon name="icon-external-link" className="h-6 w-6 fill-blue-400" />
      PDFを表示
    </a>
  );
};

type CorrectAnswerProps = {
  correctValue: string;
};

const CorrectAnswer = ({ correctValue }: CorrectAnswerProps) => {
  return (
    <div className="flex items-center gap-[14px] flex-nowrap">
      <span className="rounded-[4px] border border-solid border-gray-700 px-[6px] py-[2px] text-[12px] leading-[1.3] text-gray-900 whitespace-nowrap flex-shrink-0">
        正答
      </span>
      <span className="text-[14px] leading-[1.3] text-gray-900">
        {correctValue}
      </span>
    </div>
  );
};

type AnswerBoxProps = {
  answer?: string;
  correct?: boolean | string | null;
  answerHistories?: Array<{
    attemptNumber?: number;
    current?: boolean;
    answer?: string;
    correct?: boolean | null;
  }>;
  hasAnswerHistories?: boolean;
  currentAttemptNumber?: number;
  isHistoryExpanded: boolean;
  setIsHistoryExpanded: (expanded: boolean) => void;
};

const AnswerBox = ({
  answer,
  correct,
  answerHistories,
  hasAnswerHistories,
  currentAttemptNumber,
  isHistoryExpanded,
  setIsHistoryExpanded,
}: AnswerBoxProps) => {
  return (
    <div className="flex flex-col rounded-[4px] border border-solid border-gray-400">
      <div className="flex items-center justify-between p-[8px]">
        <div className="flex items-center gap-[14px] flex-nowrap">
          <span className="rounded-[4px] bg-gray-400 px-[6px] py-[2px] text-[12px] leading-[1.3] text-gray-900 whitespace-nowrap flex-shrink-0">
            回答
          </span>
          {currentAttemptNumber !== undefined && (
            <span className="text-[12px] leading-[1.3] text-gray-900 whitespace-nowrap flex-shrink-0">
              {currentAttemptNumber}回目
            </span>
          )}
          <div className="flex items-center gap-[6px]">
            {typeof correct === "boolean" && (
              <div className="flex-shrink-0">
                <AnswerIcon correct={correct as boolean} />
              </div>
            )}
            <span className="text-[14px] leading-[1.4] text-gray-900">
              {answer}
            </span>
          </div>
        </div>

        {hasAnswerHistories && (
          <button
            type="button"
            className="flex items-center text-[12px] font-bold leading-[1.3] text-blue-400"
            onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
          >
            <span className="hidden md:inline">回答履歴を表示</span>
            <Icon
              name="icon-arrow-down"
              className={classNames(
                "h-6 w-6 fill-blue-400 transition-transform",
                {
                  "rotate-180": isHistoryExpanded,
                },
              )}
            />
          </button>
        )}
      </div>

      {isHistoryExpanded && hasAnswerHistories && (
        <>
          <div className="mx-[8px] h-px bg-gray-400" />
          <div className="flex flex-col gap-[10px] p-[8px]">
            {answerHistories?.map((history, index) => (
              <div
                key={index}
                className={classNames(
                  "flex items-center gap-[14px] flex-nowrap px-[8px] py-[4px] rounded-[6px]",
                  {
                    "bg-[#fdf9ed]": history.current,
                  },
                )}
              >
                <span className="text-[12px] leading-[1.3] text-gray-900 whitespace-nowrap flex-shrink-0">
                  {history.attemptNumber}回目
                </span>
                <div className="flex items-center gap-[6px] flex-nowrap">
                  {typeof history.correct === "boolean" && (
                    <div className="flex-shrink-0">
                      <AnswerIcon correct={history.correct as boolean} />
                    </div>
                  )}
                  <span className="text-[14px] leading-[1.4] text-gray-900">
                    {history.answer}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const AnswerIcon = ({ correct }: { correct: boolean }) => {
  if (correct) {
    // 正解アイコン: 青い丸（○）- Figma定義
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="8" cy="8" r="5.75" stroke="#2F6AED" strokeWidth="2.5" />
      </svg>
    );
  }
  // 不正解アイコン: 赤いバツ（×）- Figma定義
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.6598 2.34009L2.33984 13.6601"
        stroke="#F15360"
        strokeWidth="2.5"
        strokeMiterlimit="10"
      />
      <path
        d="M13.6598 13.6601L2.33984 2.34009"
        stroke="#F15360"
        strokeWidth="2.5"
        strokeMiterlimit="10"
      />
    </svg>
  );
};
